/**
 * THE sidebar-mode register — `expanded` | `collapsed` | `hover`, persisted.
 *
 * NOTE: the sidebar's pixel geometry lives ONLY in `styles/tokens.css`
 * (`--app-sidebar-expanded-w` / `--app-sidebar-collapsed-w`), and no JS may
 * write a width. Both the pre-hydration script and the React effect set only
 * `data-sidebar-mode` on <html>; the tokens derive `--app-content-inset` from
 * it. Three disagreeing sources of these numbers is what made the anti-flash
 * script itself cause a cold-load content jump. Do not reintroduce a px literal.
 */

export type SidebarMode = 'expanded' | 'collapsed' | 'hover';

export const DEFAULT_SIDEBAR_MODE_KEY = 'sidebar-mode';
const LEGACY_EXPANDED_KEY = 'sidebar-expanded';

function isSidebarMode(value: unknown): value is SidebarMode {
  return value === 'expanded' || value === 'collapsed' || value === 'hover';
}

/** Read an app's current sidebar mode from localStorage. */
export function readSidebarMode(
  storageKey = DEFAULT_SIDEBAR_MODE_KEY,
  defaultMode: SidebarMode = 'expanded',
): SidebarMode {
  if (typeof window === 'undefined') return defaultMode;
  try {
    const mode = localStorage.getItem(storageKey);
    if (isSidebarMode(mode)) return mode;
    // Migrate the shared register from the old boolean format. App-specific
    // registers intentionally start from their own default.
    if (storageKey === DEFAULT_SIDEBAR_MODE_KEY) {
      const legacy = localStorage.getItem(LEGACY_EXPANDED_KEY);
      if (legacy === 'true') return 'expanded';
      if (legacy === 'false') return 'collapsed';
    }
  } catch {
    // Hardened/private browser contexts — fall through to the default.
  }
  return defaultMode;
}

/** Persist sidebar state and notify listeners. */
export function persistSidebarState(
  mode: SidebarMode,
  isVisuallyExpanded: boolean,
  storageKey = DEFAULT_SIDEBAR_MODE_KEY,
) {
  try {
    localStorage.setItem(storageKey, mode);
    if (storageKey === DEFAULT_SIDEBAR_MODE_KEY) {
      localStorage.setItem(LEGACY_EXPANDED_KEY, String(isVisuallyExpanded));
    }
  } catch {
    // Persistence is best-effort; the current document still changes.
  }
  window.dispatchEvent(new CustomEvent('sidebar-state-changed'));
}
